import { Region } from "./protocol"

// Interval-set algebra over the session timeline. The annotation UI uses it
// to keep the painted regions sorted and disjoint, which is what the
// database's ideal_regions constraint expects. The same operations are what
// x_missed = |I \ E| and x_excess = |E \ I| are built from, so this is
// shared rather than living in the client.

/** Sorts, merges anything overlapping or touching, and drops empty spans. */
export function normalize(regions: Region[]): Region[] {
  const sorted = regions
    .filter((r) => r.endMs > r.startMs)
    .sort((a, b) => a.startMs - b.startMs)
  const out: Region[] = []
  for (const r of sorted) {
    const last = out[out.length - 1]
    if (last && r.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, r.endMs)
    } else {
      out.push({ ...r })
    }
  }
  return out
}

export function union(regions: Region[], add: Region): Region[] {
  return normalize([...regions, add])
}

/** Removes `cut` from every region, splitting any region it falls inside. */
export function difference(regions: Region[], cut: Region): Region[] {
  if (cut.endMs <= cut.startMs) return normalize(regions)
  const out: Region[] = []
  for (const r of normalize(regions)) {
    if (cut.endMs <= r.startMs || cut.startMs >= r.endMs) {
      out.push(r)
      continue
    }
    if (cut.startMs > r.startMs) out.push({ startMs: r.startMs, endMs: cut.startMs })
    if (cut.endMs < r.endMs) out.push({ startMs: cut.endMs, endMs: r.endMs })
  }
  return out
}

/** Everything in `regions` that no region of `cuts` covers. */
export function subtract(regions: Region[], cuts: Region[]): Region[] {
  return normalize(cuts).reduce(difference, normalize(regions))
}

export function totalMs(regions: Region[]): number {
  return normalize(regions).reduce((sum, r) => sum + r.endMs - r.startMs, 0)
}

/**
 * The two error rates of model_spec.md, in milliseconds per minute.
 *
 * `missed` is annotated speech no segment covered — a late onset, an early
 * cutoff, or a segment that never fired. `excess` is emitted audio outside
 * every annotated region. Dividing by the length of the recording is what
 * makes a long session and a short one comparable.
 */
export function errorRates(
  ideal: Region[],
  emitted: Region[],
  durationMs: number
): { missed: number; excess: number } {
  const minutes = durationMs / 60000
  return {
    missed: totalMs(subtract(ideal, emitted)) / minutes,
    excess: totalMs(subtract(emitted, ideal)) / minutes,
  }
}
